import { EllipsisHorizontalIcon } from "@heroicons/react/16/solid";
import clsx from "clsx";
import { ReactElement } from "react";
import { Controller, FieldArrayWithId, useFormContext } from "react-hook-form";

import { HasHttpParameters } from "../types/form-inputs.ts";
import { Dropdown } from "./catalyst/Dropdown.tsx";
import { DropdownButton } from "./catalyst/DropdownButton.tsx";
import { DropdownItem } from "./catalyst/DropdownItem.tsx";
import { DropdownMenu } from "./catalyst/DropdownMenu.tsx";
import { ErrorMessage } from "./catalyst/ErrorMessage.tsx";
import { Field } from "./catalyst/Field.tsx";
import { Input } from "./catalyst/Input.tsx";
import { TableCell } from "./catalyst/TableCell.tsx";
import { TableRow } from "./catalyst/TableRow.tsx";
import EntityAwareIntellisenseEditor from "./json/EntityAwareIntellisenseEditor.tsx";

interface ApiParameterRowProps {
  index: number;
  parameter: FieldArrayWithId<HasHttpParameters, "httpParameters", "id">;
  remove?: (index: number) => void;
  showDescription: boolean;
}
const APIParameterRow = ({
  index,
  parameter,
  remove,
  showDescription = false,
}: ApiParameterRowProps): ReactElement => {
  const {
    control,
    formState: { errors },
    register,
  } = useFormContext<HasHttpParameters>();

  return (
    <TableRow>
      <TableCell className="w-1/5 align-top">
        <Field>
          <Input
            {...register(`httpParameters.${index}.name`, {
              required: "The name is required",
            })}
            defaultValue={parameter.name}
            invalid={!!errors?.httpParameters?.[index]?.name}
            placeholder="Name"
          />
          {errors?.httpParameters?.[index]?.name && (
            <ErrorMessage>
              {errors.httpParameters[index].name.message}
            </ErrorMessage>
          )}
        </Field>
      </TableCell>

      <TableCell
        className={clsx("align-top", showDescription ? "w-2/5" : "w-4/5")}
      >
        <Field>
          <Controller
            control={control}
            defaultValue=""
            name={`httpParameters.${index}.value`}
            render={({ field: { onChange, value } }) => (
              <EntityAwareIntellisenseEditor
                bordered={true}
                fieldName={`httpParameters.${index}.value`}
                language="plaintext"
                noMargin={true}
                onChange={(newValue) => {
                  onChange(newValue || "");
                }}
                readOnly={false}
                singleLine={true}
                value={value || ""}
              />
            )}
            rules={{
              required: "The value is required",
            }}
          />
          {errors?.httpParameters?.[index]?.value && (
            <ErrorMessage>
              {errors.httpParameters[index].value.message}
            </ErrorMessage>
          )}
        </Field>
      </TableCell>

      {showDescription && (
        <TableCell className="w-2/5 align-top">
          <Field>
            <Input
              {...register(`httpParameters.${index}.description`)}
              defaultValue={parameter.description ?? ""}
              invalid={!!errors?.httpParameters?.[index]?.description}
              placeholder="Description"
            />
            {errors?.httpParameters?.[index]?.description && (
              <ErrorMessage>
                {errors.httpParameters[index].description.message}
              </ErrorMessage>
            )}
          </Field>
        </TableCell>
      )}
      <TableCell className="w-10 text-right align-top">
        {remove && (
          <Dropdown>
            <DropdownButton aria-label="More options" plain>
              <EllipsisHorizontalIcon />
            </DropdownButton>
            <DropdownMenu>
              {remove && (
                <DropdownItem
                  className="hover:bg-red-500 hover:text-white"
                  onClick={() => remove(index)}
                >
                  Delete
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        )}
      </TableCell>
    </TableRow>
  );
};

export default APIParameterRow;
